import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { ChevronDown, ChevronUp } from 'lucide-react';
import SupportEmailPopup from '../components/SupportEmailPopup';

const faqs = [
  {
    category: 'General',
    questions: [
      {
        q: 'What is RazientSounds?',
        a: 'RazientSounds is an artist roster and booking platform. Browse our verified musicians, DJs, bands and performers, send a booking inquiry, and our team coordinates everything with you — no booking fees.'
      },
      {
        q: 'How do I book an artist?',
        a: 'Browse the roster, open an artist profile, and hit "Book Through Us" with your event date and details. An open chat connects you, the artist and the RazientSounds team so everything is coordinated in one place.'
      },
      {
        q: 'Does it cost anything to book through the site?',
        a: 'No. RazientSounds does not charge booking fees or take a cut. Rates and payment arrangements are agreed directly during the booking conversation with our team.'
      }
    ]
  },
  {
    category: 'For Users',
    questions: [
      {
        q: 'Can I cancel or move my booking?',
        a: 'Yes — just say so in your booking chat. Our team will confirm, move the date, or cancel with the artist for you.'
      },
      {
        q: 'How do I contact an artist?',
        a: 'Once you send a booking inquiry, a chat thread opens with the artist and the RazientSounds team. Use it to coordinate event details, arrival times and requirements.'
      },
      {
        q: 'What if the artist cancels?',
        a: 'If an artist has to cancel, our team will let you know right away and help you find a replacement from the roster.'
      }
    ]
  },
  {
    category: 'For Artists',
    questions: [
      {
        q: 'How do I join the roster?',
        a: 'Create an account, click "Become an Artist," fill out your profile with genres (riddim, dubstep, deepdub, experimental and more), upload photos/videos, and add your music links. Admin approval is required before you appear publicly.'
      },
      {
        q: 'How do bookings reach me?',
        a: 'When a client sends an inquiry for you, a team-managed chat opens. You and the RazientSounds team coordinate the date, logistics and terms together.'
      },
      {
        q: 'Can I decline bookings?',
        a: 'Yes! You have full control to accept or decline booking requests through your artist dashboard.'
      },
      {
        q: 'Are there platform fees?',
        a: 'No — RazientSounds is a roster, not a middleman. We don\'t take a commission on your bookings.'
      }
    ]
  },
  {
    category: 'The Virtual Club & City',
    questions: [
      {
        q: 'What is the Nightclub?',
        a: 'A live 2D world where you can walk around, chat, dance and hang out with other fans — plus explore the city: shops, the Turtle Shack beach bar, apartments, mansions and Sunset Shore beach.'
      },
      {
        q: 'What are VibeCoins?',
        a: 'The in-club currency. Earn them by playing and use them for drinks, food, outfits, furniture for your apartment or house, arcade plays and more.'
      },
      {
        q: 'How do live streams work?',
        a: 'Artists and fans can Go Live from the Live page. Anyone can join a stream to watch and chat — and the amphitheater screen at Sunset Shore shows whoever is live in the city.'
      }
    ]
  },
  {
    category: 'Technical Support',
    questions: [
      {
        q: 'I forgot my password. What should I do?',
        a: 'If you signed up with Google, use "Sign in with Google" and Google\'s account recovery. Otherwise contact support and we\'ll help you get back in.'
      },
      {
        q: 'The website is not loading correctly',
        a: 'Try clearing your browser cache, disabling browser extensions, or using a different browser. Contact support if issues persist.'
      },
      {
        q: 'How do I delete my account?',
        a: 'Contact support at support@razientsounds.com to request account deletion. Please note this cannot be undone.'
      }
    ]
  }
];

function FAQItem({ question, answer }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="glass-light rounded-lg overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full p-4 flex items-center justify-between text-left hover:bg-white/5 transition"
      >
        <span className="font-semibold text-white">{question}</span>
        {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
      </button>
      {isOpen && (
        <div className="p-4 pt-0 text-white/70 leading-relaxed">
          {answer}
        </div>
      )}
    </div>
  );
}

export default function FAQ() {
  const [showSupport, setShowSupport] = useState(false);

  return (
    <div className="min-h-screen relative pt-20">
      <div className="container mx-auto px-4 py-8 relative z-10 max-w-4xl">
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold gradient-text mb-4">Frequently Asked Questions</h1>
          <p className="text-white/60 text-lg">Find answers to common questions about RazientSounds</p>
        </div>

        <div className="space-y-8">
          {faqs.map((category, idx) => (
            <Card key={idx} className="glass-default border-white/10">
              <CardContent className="p-6">
                <h2 className="text-2xl font-bold text-violet-400 mb-4">{category.category}</h2>
                <div className="space-y-3">
                  {category.questions.map((faq, qIdx) => (
                    <FAQItem key={qIdx} question={faq.q} answer={faq.a} />
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card className="glass-default border-white/10 mt-8">
          <CardContent className="p-8 text-center">
            <h3 className="text-2xl font-bold mb-4">Still have questions?</h3>
            <p className="text-white/60 mb-4">Can&apos;t find what you&apos;re looking for? We&apos;re here to help!</p>
            <button
              onClick={() => setShowSupport(true)}
              data-testid="faq-contact-support-btn"
              className="inline-block px-8 py-3 rounded-full bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 font-semibold transition text-white"
            >
              Contact Support
            </button>
          </CardContent>
        </Card>
      </div>
      <SupportEmailPopup open={showSupport} onClose={() => setShowSupport(false)} />
    </div>
  );
}
